import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { Row, Col, Alert, Container } from "reactstrap";
import * as Yup from "yup";
import { useFormik } from "formik";
import { useSelector, useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { registerUser, apiError } from "../../store/actions";
import logolight from '../../assets/images/logo-removebg-preview.png';
import logodark from '../../assets/images/logo-removebg-preview.png';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import CircularProgress from '@mui/material/CircularProgress';

const Register = (props) => {
  document.title = "Register | LuxBillingEHR";

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const toggleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      email: '',
      username: '',
      password: '',
    },
    validationSchema: Yup.object({
      email: Yup.string().required("Please Enter Your Email"),
      username: Yup.string().required("Please Enter Your Username"),
      password: Yup.string().required("Please Enter Your Password"),
    }),
    onSubmit: (values) => {
      setLoading(true);
      dispatch(registerUser(values));
    },
  });

  const { user, registrationError } = useSelector((state) => ({
    user: state.account.user,
    registrationError: state.account.registrationError,
  }));

  useEffect(() => {
    dispatch(apiError(""));
    document.body.className = "bg-pattern";
    document.body.style.overflow = "hidden";
    return function cleanup() {
      document.body.className = "";
      document.body.style.overflow = "";
    };
  }, [dispatch]);

  const getInputStyles = (field) => ({
    width: "100%",
    padding: "8px",
    background: isHovered && field === "password"
      ? "linear-gradient(90deg, rgba(37, 99, 235, 0.1), rgba(139, 92, 246, 0.1))"
      : "transparent",
    border: validation.touched[field] && validation.errors[field]
      ? "1px solid #dc3545"
      : isFocused && field === "password"
      ? "1px solid #2563eb"
      : "1px solid rgba(255, 255, 255, 0.5)",
    borderRadius: "4px",
    fontSize: "16px",
    color: isFocused && field === "password" ? "#ffffff" : "transparent",
    outline: "none",
    transition: "border-color 0.3s, box-shadow 0.3s, background 0.3s",
    caretColor: "#ffffff",
    boxShadow: isHovered && field === "password" ? "0 0 8px rgba(37, 99, 235, 0.5)" : "none",
  });

  return (
    <React.Fragment>
      <div
        className="bg-overlay"
        style={{
          background: "linear-gradient(135deg, rgba(0, 0, 255, 0.2), rgba(0, 255, 255, 0.2))",
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          zIndex: -1,
          overflow: "hidden",
        }}
      >
        {/* Animated Billing Cards */}
        <div
          className="billing-card"
          style={{
            position: "absolute",
            top: "10%",
            left: "5%",
            animation: "floatUp 8s ease-in-out infinite",
          }}
        >
          <div
            style={{
              background: "rgba(255, 255, 255, 0.2)",
              backdropFilter: "blur(5px)",
              borderRadius: "12px",
              padding: "20px",
              boxShadow: "0 4px 15px rgba(0, 0, 0, 0.2)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "120px",
              height: "80px",
            }}
          >
            <i className="mdi mdi-credit-card" style={{ fontSize: "40px", color: "#ffffff" }}></i>
          </div>
        </div>
        <div
          className="billing-card"
          style={{
            position: "absolute",
            top: "60%",
            right: "10%",
            animation: "floatUp 9s ease-in-out infinite",
          }}
        >
          <div
            style={{
              background: "rgba(255, 255, 255, 0.2)",
              backdropFilter: "blur(5px)",
              borderRadius: "12px",
              padding: "20px",
              boxShadow: "0 4px 15px rgba(0, 0, 0, 0.2)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "120px",
              height: "80px",
            }}
          >
            <i className="mdi mdi-receipt" style={{ fontSize: "40px", color: "#ffffff" }}></i>
          </div>
        </div>
        <div
          className="billing-card"
          style={{
            position: "absolute",
            bottom: "15%",
            left: "20%",
            animation: "floatUp 10s ease-in-out infinite",
          }}
        >
          <div
            style={{
              background: "rgba(255, 255, 255, 0.2)",
              backdropFilter: "blur(5px)",
              borderRadius: "12px",
              padding: "20px",
              boxShadow: "0 4px 15px rgba(0, 0, 0, 0.2)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "120px",
              height: "80px",
            }}
          >
            <i className="mdi mdi-currency-usd" style={{ fontSize: "40px", color: "#ffffff" }}></i>
          </div>
        </div>
        <div
          className="billing-card"
          style={{
            position: "absolute",
            top: "20%",
            right: "15%",
            animation: "floatUp 8.5s ease-in-out infinite",
          }}
        >
          <div
            style={{
              background: "rgba(255, 255, 255, 0.2)",
              backdropFilter: "blur(5px)",
              borderRadius: "12px",
              padding: "20px",
              boxShadow: "0 4px 15px rgba(0, 0, 0, 0.2)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "120px",
              height: "80px",
            }}
          >
            <i className="mdi mdi-wallet" style={{ fontSize: "40px", color: "#ffffff" }}></i>
          </div>
        </div>
        <div
          className="billing-card"
          style={{
            position: "absolute",
            bottom: "25%",
            left: "10%",
            animation: "floatUp 9.5s ease-in-out infinite",
          }}
        >
          <div
            style={{
              background: "rgba(255, 255, 255, 0.2)",
              backdropFilter: "blur(5px)",
              borderRadius: "12px",
              padding: "20px",
              boxShadow: "0 4px 15px rgba(0, 0, 0, 0.2)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "120px",
              height: "80px",
            }}
          >
            <i className="mdi mdi-bank" style={{ fontSize: "40px", color: "#ffffff" }}></i>
          </div>
        </div>
        <div
          className="billing-card"
          style={{
            position: "absolute",
            top: "30%",
            left: "40%",
            animation: "floatUp 8.2s ease-in-out infinite",
          }}
        >
          <div
            style={{
              background: "rgba(255, 255, 255, 0.2)",
              backdropFilter: "blur(5px)",
              borderRadius: "12px",
              padding: "20px",
              boxShadow: "0 4px 15px rgba(0, 0, 0, 0.2)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "120px",
              height: "80px",
            }}
          >
            <i className="mdi mdi-calculator" style={{ fontSize: "40px", color: "#ffffff" }}></i>
          </div>
        </div>
        <div
          className="billing-card"
          style={{
            position: "absolute",
            bottom: "10%",
            right: "30%",
            animation: "floatUp 9.2s ease-in-out infinite",
          }}
        >
          <div
            style={{
              background: "rgba(255, 255, 255, 0.2)",
              backdropFilter: "blur(5px)",
              borderRadius: "12px",
              padding: "20px",
              boxShadow: "0 4px 15px rgba(0, 0, 0, 0.2)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "120px",
              height: "80px",
            }}
          >
            <i className="mdi mdi-cart" style={{ fontSize: "40px", color: "#ffffff" }}></i>
          </div>
        </div>
        <div
          className="billing-card"
          style={{
            position: "absolute",
            top: "50%",
            left: "30%",
            animation: "floatUp 8.8s ease-in-out infinite",
          }}
        >
          <div
            style={{
              background: "rgba(255, 255, 255, 0.2)",
              backdropFilter: "blur(5px)",
              borderRadius: "12px",
              padding: "20px",
              boxShadow: "0 4px 15px rgba(0, 0, 0, 0.2)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "120px",
              height: "80px",
            }}
          >
            <i className="mdi mdi-cash" style={{ fontSize: "40px", color: "#ffffff" }}></i>
          </div>
        </div>
      </div>
      <div
        className="account-pages"
        style={{
          minHeight: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          padding: "20px",
          overflow: "hidden",
          marginTop: "5vh",
        }}
      >
        <div
          style={{
            width: "100%",
            maxWidth: window.innerWidth < 640 ? "95%" : window.innerWidth < 768 ? "90%" : window.innerWidth < 1200 ? "60%" : "33.33%",
            margin: "0 auto",
          }}
        >
          <div
            style={{
              background: "rgba(255, 255, 255, 0.1)",
              backdropFilter: "blur(10px)",
              borderRadius: "8px",
              boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
              border: "1px solid rgba(255, 255, 255, 0.3)",
            }}
          >
            <div
              style={{
                padding: window.innerWidth < 640 ? "16px" : "32px",
              }}
            >
              <div style={{ textAlign: "center" }}>
                <Link to="/">
                  <span>
                    <img
                      src={logolight}
                      alt="logo-light"
                      style={{
                        height: "40px",
                        marginTop: "-25px",
                        animation: "flip 4s infinite linear",
                      }}
                    />
                  </span>
                </Link>
              </div>
              <h4
                style={{
                  fontSize: window.innerWidth < 640 ? "16px" : "18px",
                  color: "#ffffff",
                  marginTop: "8px",
                  textAlign: "center",
                }}
              >
                Free Register
              </h4>
              <p
                style={{
                  color: "rgba(255, 255, 255, 0.7)",
                  textAlign: "center",
                  marginBottom: "24px",
                }}
              >
                Get your free LuxBilling account now.
              </p>
              <form
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "16px",
                }}
                onSubmit={(e) => {
                  e.preventDefault();
                  validation.handleSubmit();
                  return false;
                }}
              >
                {loading ? (
                  <div
                    style={{
                      margin: "5rem",
                      textAlign: "center",
                    }}
                  >
                    <CircularProgress style={{ color: "#ffffff" }} />
                    <p style={{ color: "#ffffff" }}>Loading...</p>
                  </div>
                ) : (
                  <>
                    {user ? (
                      <Alert
                        style={{
                          background: "rgba(40, 167, 69, 0.9)",
                          color: "#ffffff",
                          padding: "10px",
                          borderRadius: "4px",
                          marginBottom: "16px",
                        }}
                      >
                        Register User Successfully
                      </Alert>
                    ) : null}
                    {registrationError ? (
                      <Alert
                        style={{
                          background: "rgba(220, 53, 69, 0.9)",
                          color: "#ffffff",
                          padding: "10px",
                          borderRadius: "4px",
                          marginBottom: "16px",
                        }}
                      >
                        <div>{registrationError}</div>
                      </Alert>
                    ) : null}
                    <div>
                      <style>
{`
  .transparent-input {
    width: 100%;
    padding: 8px;
    background: transparent;
    border: 1px solid rgba(255, 255, 255, 0.5);
    border-radius: 6px;
    font-size: 16px;
    color: transparent;
    outline: none;
    transition: border-color 0.3s, box-shadow 0.3s, background 0.3s, color 0.3s;
    caret-color: #ffffff;
  }

  .transparent-input::placeholder {
    color: #d1d5db;
  }

  .transparent-input:focus {
    border-color: #2563eb;
    color: #ffffff;
    background: rgba(37, 99, 235, 0.05);
  }

  .transparent-input:hover {
    box-shadow: 0 0 8px rgba(37, 99, 235, 0.5);
    background: linear-gradient(
      90deg,
      rgba(37, 99, 235, 0.1),
      rgba(139, 92, 246, 0.1)
    );
  }

  .animated-gradient {
    background: linear-gradient(270deg, #2563eb, #8b5cf6, #2563eb);
    background-size: 600% 600%;
    animation: gradientMove 6s ease infinite;
    border-radius: 16px;
    color: #fff;
    padding: 12px;
    border: none;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    transition: transform 0.2s, box-shadow 0.3s;
  }

  @keyframes gradientMove {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }

  @keyframes floatUp {
    0% { opacity: 0; transform: translateY(0); }
    10% { opacity: 1; }
    50% { opacity: 1; transform: translateY(-50px); }
    90% { opacity: 0; }
    100% { opacity: 0; transform: translateY(0); }
  }

  @keyframes flip {
    0% { transform: rotateY(0deg); }
    100% { transform: rotateY(360deg); }
  }
`}
</style>

                      <div style={{ marginBottom: "16px" }}>
                        <label
                          style={{
                            display: "block",
                            fontSize: "14px",
                            color: "#ffffff",
                            marginBottom: "8px",
                          }}
                        >
                          Email
                        </label>
                        <input
                          name="email"
                          placeholder="Enter email"
                          type="email"
                          className="transparent-input"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.email || ""}
                          style={{
                            border: validation.touched.email && validation.errors.email
                              ? "1px solid #dc3545"
                              : undefined,
                          }}
                        />
                        {validation.touched.email && validation.errors.email ? (
                          <div
                            style={{
                              color: "#fecaca",
                              fontSize: "12px",
                              marginTop: "4px",
                            }}
                          >
                            <div>{validation.errors.email}</div>
                          </div>
                        ) : null}
                      </div>

                      <div style={{ marginBottom: "16px" }}>
                        <label
                          style={{
                            display: "block",
                            fontSize: "14px",
                            color: "#ffffff",
                            marginBottom: "8px",
                          }}
                        >
                          Username
                        </label>
                        <input
                          name="username"
                          placeholder="Enter username"
                          type="text"
                          className="transparent-input"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.username || ""}
                          style={{
                            border: validation.touched.username && validation.errors.username
                              ? "1px solid #dc3545"
                              : undefined,
                          }}
                        />
                        {validation.touched.username && validation.errors.username ? (
                          <div
                            style={{
                              color: "#fecaca",
                              fontSize: "12px",
                              marginTop: "4px",
                            }}
                          >
                            <div>{validation.errors.username}</div>
                          </div>
                        ) : null}
                      </div>

                      <div style={{ marginBottom: "16px" }}>
                        <label
                          style={{
                            display: "block",
                            fontSize: "14px",
                            color: "#ffffff",
                            marginBottom: "8px",
                          }}
                        >
                          Password
                        </label>
                        <div
                          style={{
                            position: "relative",
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <input
                            name="password"
                            value={validation.values.password || ""}
                            type={showPassword ? "text" : "password"}
                            placeholder="Enter Password"
                            onChange={validation.handleChange}
                            onBlur={(e) => {
                              validation.handleBlur(e);
                              setIsFocused(false);
                            }}
                            onFocus={() => setIsFocused(true)}
                            onMouseOver={() => setIsHovered(true)}
                            onMouseOut={() => setIsHovered(false)}
                            style={getInputStyles("password")}
                          />
                          <span
                            onClick={toggleShowPassword}
                            style={{
                              position: "absolute",
                              right: "8px",
                              cursor: "pointer",
                              color: "#d1d5db",
                            }}
                            onMouseOver={(e) => (e.currentTarget.style.color = "#2563eb")}
                            onMouseOut={(e) => (e.currentTarget.style.color = "#d1d5db")}
                          >
                            {showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                          </span>
                        </div>
                        {validation.touched.password && validation.errors.password ? (
                          <div
                            style={{
                              color: "#fecaca",
                              fontSize: "12px",
                              marginTop: "4px",
                            }}
                          >
                            <div>{validation.errors.password}</div>
                          </div>
                        ) : null}
                      </div>

                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          color: "#ffffff",
                          fontSize: "14px",
                        }}
                      >
                        <input
                          type="checkbox"
                          id="term-conditionCheck"
                          style={{ cursor: "pointer" }}
                        />
                        <label
                          htmlFor="term-conditionCheck"
                          style={{ margin: 0, cursor: "pointer" }}
                        >
                          I accept{" "}
                          <Link
                            to="#"
                            style={{
                              color: "#2563eb",
                              textDecoration: "none",
                            }}
                            onMouseOver={(e) => (e.currentTarget.style.color = "#1d4ed8")}
                            onMouseOut={(e) => (e.currentTarget.style.color = "#2563eb")}
                          >
                            Terms and Conditions
                          </Link>
                        </label>
                      </div>

                      <div style={{ display: "grid", marginTop: "16px" }}>
                        <button
                          type="submit"
                          className="animated-gradient"
                          onMouseOver={(e) => {
                            e.currentTarget.style.transform = "scale(1.05)";
                            e.currentTarget.style.boxShadow = "0 0 12px rgba(139, 92, 246, 0.5)";
                          }}
                          onMouseOut={(e) => {
                            e.currentTarget.style.transform = "scale(1)";
                            e.currentTarget.style.boxShadow = "none";
                          }}
                          onMouseDown={(e) => (e.currentTarget.style.transform = "scale(0.98)")}
                          onMouseUp={(e) => (e.currentTarget.style.transform = "scale(1.05)")}
                        >
                          Register
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </form>
            </div>
          </div>
          <div
            style={{
              textAlign: "center",
              color: "rgba(255, 255, 255, 0.5)",
              marginTop: "24px",
            }}
          >
            <p>
              Already have an account?{" "}
              <Link
                to="/login"
                style={{
                  fontWeight: "500",
                  color: "#2563eb",
                  textDecoration: "none",
                }}
                onMouseOver={(e) => (e.currentTarget.style.color = "#1d4ed8")}
                onMouseOut={(e) => (e.currentTarget.style.color = "#2563eb")}
              >
                Login
              </Link>
            </p>
            <p>
              © {new Date().getFullYear()} Upzet. Crafted with{" "}
              <i style={{ color: "#dc3545" }} className="mdi mdi-heart"></i> by
              Themesdesign
            </p>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default Register;

Register.propTypes = {
  history: PropTypes.object,
};